// Package imports
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { find, findIndex } from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@mui/material/Grid";

// Custom imports
import ContinueCard from "../../Components/Card/ContinueCard";
import { getContinueTeaching } from "./Services";
import { getClassNames } from "../../Js/Common";
import { currentAssetSliceActions } from "../../Store/Slices/CurrentAssetSlice";
import { commonSliceActions } from "../../Store/Slices/CommonSlice";
import { Typography } from "@mui/material";
import { productSliceActions } from "../../Store/Slices/ProductSlice";

// Global variables

// Component
const ContinueTeaching = () => {
  // Hooks
  const dispatch = useDispatch();
  const navigation = useNavigate();

  // State
  const [continueData, setContinueData] = useState([]);
  const [classes, setClasses] = useState([]);

  // Redux state
  const userInfo = useSelector((state) => state.loggedInUserInfo.userInfo);
  const productData = useSelector((state) => state.product.productData);

  // React logic hooks
  useEffect(() => {
    const fetchContinueTeaching = async () => {
      let data = await getContinueTeaching(userInfo.uid, userInfo.site_uuid);
      setContinueData(data.data);
      setClasses(await getClassNames(userInfo.site_uuid));
    };
    fetchContinueTeaching();
  }, []);

  // Get class name for given id
  const getClassNameFromUid = (id) => {
    if (!id || id == "null" || id == "self") {
      return "Self";
    }
    let classname = find(classes, { id: id });
    if (classname) {
      return classname.value;
    } else {
      return null;
    }
  };

  // Get module & session name for give leaf node
  const getSessionAndModule = (id) => {
    if (productData) {
      let tempProduct = JSON.parse(productData[0]["course_data"]);
      tempProduct = tempProduct.data[0]["data"];
      let pathArray = id.split("_");
      let actualLength = pathArray.length;
      if (actualLength > 7) {
        pathArray = pathArray.slice(0, 6);
      } else {
        pathArray = pathArray.slice(0, 5);
      }
      let rootId = pathArray.slice(0, 3);
      pathArray.splice(0, 3, rootId.join("_"));
      let moduleObj = find(tempProduct, {
        id: pathArray[0] + "_" + pathArray[1],
      });
      if (actualLength > 7) {
        moduleObj = find(moduleObj.data, {
          id: pathArray[0] + "_" + pathArray[1] + "_" + pathArray[2],
        });
      }
      let sessionObj;
      if (moduleObj && moduleObj.data) {
        sessionObj = find(moduleObj.data, { id: pathArray.join("_") });
      }

      return {
        module: moduleObj ? moduleObj.value : "",
        session: sessionObj ? sessionObj.value : "",
      };
    }
  };

  // Get module & session name for give leaf node
  const getBreadCrumb = (id, language, languageIndex) => {
    if (productData) {
      let tempProduct = JSON.parse(productData[0]["course_data"]);
      if (language) {
        tempProduct = tempProduct.data[0]["data"][languageIndex]["data"];
      } else {
        tempProduct = tempProduct.data[0]["data"];
      }
      let pathArray = id.split("_");
      let rootId;
      if (language) {
        rootId = pathArray.slice(0, 4);
      } else {
        rootId = pathArray.slice(0, 3);
      }
      pathArray.shift();
      pathArray.shift();
      pathArray.shift();
      if (language) {
        pathArray.shift();
      }
      pathArray.unshift(rootId.join("_"));
      let module = find(tempProduct, { id: `${pathArray[0]}_${pathArray[1]}` });
      let session = find(module.data, {
        id: `${pathArray[0]}_${pathArray[1]}_${pathArray[2]}`,
      });
      let node = find(session.data, {
        id: `${pathArray[0]}_${pathArray[1]}_${pathArray[2]}_${pathArray[3]}`,
      });
      let leaf = find(node.data, { id: id });
      console.log(module.value);
      console.log(session.value);
      console.log(node.value);
      console.log(leaf.value);
      if (language) {
        return `${language} | ${module.value} | ${session.value} | ${node.value} | ${leaf.value}`;
      }
      return `${module.value} | ${session.value} | ${node.value} | ${leaf.value}`;
    }
  };

  const goToAsset = (obj) => {
    let classId = obj.outcome.classId;
    let assetId = obj.context.assetId;
    let splitArray = obj.outcome.leafId.split("_");
    let leafId = splitArray.join("_");
    splitArray.pop();
    let nodeId = splitArray.join("_");
    splitArray.pop();
    let sessionId = splitArray.join("_");
    splitArray.pop();
    let moduleId = splitArray.join("_");
    splitArray.pop();
    let languageTitle = null;
    let languageIndex = -1;
    if (splitArray.length > 3) {
      let tempProduct = JSON.parse(productData[0]["course_data"]);
      languageIndex = findIndex(tempProduct["data"][0]["data"], {
        id: splitArray.join("_"),
      });
      if (languageIndex > -1) {
        languageTitle = tempProduct["data"][0]["data"][languageIndex]["value"];
        dispatch(productSliceActions.setLanguage(languageIndex));
      }
    }
    let breadcrumb = getBreadCrumb(
      obj.outcome.leafId,
      languageTitle,
      languageIndex
    );
    dispatch(commonSliceActions.setBreadcrumb(breadcrumb));
    dispatch(currentAssetSliceActions.setTrainerContinueTeachingClass(classId));
    dispatch(
      currentAssetSliceActions.setTrainerContinueTeachingClassName(
        getClassNameFromUid(classId)
      )
    );
    dispatch(currentAssetSliceActions.setTrainerContinueTeachingLog(obj));
    sessionStorage.setItem("assetId", assetId);
    navigation(`/Teach/Content/${moduleId}/${sessionId}/${nodeId}/${leafId}`);
  };

  const styleClasses = useStyles();

  // JSX template
  return (
    <>
      {continueData.length > 0 && (
        <Grid sx={{ position: "relative" }}>
          <Grid
            sx={{
              color: "primary.main",
              pl: 7,
              pb: 1,
              fontWeight: "500",
              pt: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: "500",
                "@media (min-width: 1636px)": {
                  fontSize: "1.6rem",
                },
              }}
            >
              Continue Teaching
            </Typography>
          </Grid>
          <Grid
            container
            className={styleClasses.scrollBarHeight}
            sx={{
              flexDirection: "row",
              flexWrap: "nowrap",
              gap: 3,
              justifyContent: "flex-start",
              pl: 7,
              pb: 2,
              pr: 7,
              backgroundColor: "#F5F7FF",
              overflow: "auto",
              width: "95%",
              marginLeft: 4,
              borderRadius: 5,
            }}
          >
            {continueData.map((val, index) => {
              return (
                <div key={`${val.uid}_continueTeaching`}>
                  <ContinueCard
                    bottomText={getClassNameFromUid(val.outcome.classId)}
                    course={getSessionAndModule(val.outcome.leafId)}
                    press={() => goToAsset(val)}
                  />
                </div>
              );
            })}
          </Grid>
        </Grid>
      )}
    </>
  );
};

const useStyles = makeStyles({
  scrollBar: {
    "&::-webkit-scrollbar": {
      width: "0.6em",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#ffe600ff",
      "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)",
      borderRadius: 5,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#5f5600ff",
      borderRadius: 5,
    },
  },
  scrollBarHeight: {
    "&::-webkit-scrollbar": {
      height: "0.6em",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#ffe600ff",
      "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)",
      borderRadius: 5,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#5f5600ff",
      borderRadius: 5,
    },
  },
});

export default ContinueTeaching;
